import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  ArrowDropDown as ArrowDropDownIcon,
  Search as SearchIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  PhotoLibraryOutlined as PhotoLibraryOutlinedIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { SelectChangeEvent } from '@mui/material/Select';
import { catalogosService } from '../../services/catalogosService';
import tipoMonedaService, { TipoMoneda } from '../../services/tipoMonedaService';
import proveedorService, { Proveedor } from '../../services/proveedorService';
import { Ramo } from '../../types';
import ImputableSearch from '../../components/ImputableSearch';
import imputableService, { Imputable } from '../../services/imputableService';
import ProveedorSearch from '../../components/ProveedorSearch';
import variablesSistemaService from '../../services/variablesSistemaService';
import productoService from '../../services/productoService';
import { ProductoFoto, getProductoFallbackImage } from '../../utils/productImage';
import { TIPO_ARTICULO_OPTIONS } from '../../config/tipoArticuloOptions';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface Catalogos {
  ramos: any[];
  lineas: any[];
  clases: any[];
  unidadesMedida: any[];
}

interface StockDescuentoDto {
  codigoArticulo?: string;
  desc1?: number;
  desc2?: number;
  desc3?: number;
  desc4?: number;
  desc5?: number;
  costoCatalogo?: number;
}

interface ProductoFormData {
  codigoArticulo: string;
  descripcion: string;
  nombreLargo: string | null;
  codBarra: string | null;
  codigoProveedor: string | null;
  codProveedor: number | null;
  codigoArticuloProveedor: string | null;
  tipoArticulo: string | null;
  codigoRamo: string;
  codigoLinea: string;
  codigoClase: string;
  idUnidadMedida: string | null;
  tipoMoneda: number | null;
  precioVenta: number | null;
  precio: number | null;
  cantidad: number | null;
  stockMin: number | null;
  stockMax: number | null;
  costo: number | null;
  fechaUltCosto: string | null;
  fechaUltPrecio: string | null;
  integrar: number | null;
  calculaIva: string | null;
  ivaRI: number | null;
  ivaRN: number | null;
  ivaCF: number | null;
  ivaEX: number | null;
  ivaMT: number | null;
  impInterno1: number | null;
  impInterno2: number | null;
  tasaHidrica: number | null;
  porc_Desc: number | null;
  descuentos?: StockDescuentoDto[] | null;
  costoCatalogo: number | null;
  desc1: number | null;
  desc2: number | null;
  desc3: number | null;
  desc4: number | null;
  desc5: number | null;
  precioReceta: number | null;
  precioUnidad: number | null;
  atributo1: string | null;
  atributo2: string | null;
  atributo3: string | null;
  sustituto: string | null;
  reemplazo: string | null;
  imagen: string | null;
  lista: string | null;
  plu: string | null;
  valorOro: number | null;
  valorPlata: number | null;
  porcentajeGanan: number | null;
  porcentajeMkpminimo: number | null;
  impuestoInterno: number | null;
  almacen: number | null;
  almacenFp: string | null;
  manoObra: number | null;
  manoObraFp: string | null;
  otro: number | null;
  otrosFp: string | null;
  stockOptimo: number | null;
  stockVenta: number | null;
  stockProduccion: number | null;
  stockPedido: number | null;
  recetaUnidad: string | null;
  relacionPesoUnid: number | null;
  volumen: number | null;
  tara: number | null;
  invetariado: number | null;
  tipoCodBarra: number | null;
  coeficiente: number | null;
  nroTasaIva: number | null;
  tiempo: string | null;
  liquidaComision: number | null;
  cantOrdenCompra: number | null;
  calculoPrecio: string | null;
  noBonificable: number | null;
  umrefPed: string | null;
  umrefPrep: string | null;
  umrefFac: string | null;
  umrefStock: string | null;
  anchoCm: number | null;
  altoCm: number | null;
  largoCm: number | null;
  cantPalletUmprep: number | null;
  sincronizar: number | null;
  codigoArticuloMeli: string | null;
  ctaInventario?: number | null;
  ctaVenta?: number | null;
  ctaCompra?: number | null;
  ctaCostoVenta?: number | null;
  ctaDesc?: number | null;
  ctaCtraPartida?: number | null;
}

interface PendingProductoFoto {
  tempId: string;
  file: File;
  previewUrl: string;
  nombre: string;
}

const MAX_FOTOS_PRODUCTO = 4;
const MAX_FOTO_MB = 10;
const MAX_FOTO_BYTES = MAX_FOTO_MB * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const ALLOWED_IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.webp']);

const isSupportedImageFile = (file: File): boolean => {
  const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  const hasValidExtension = ALLOWED_IMAGE_EXTENSIONS.has(extension);
  if (!hasValidExtension) {
    return false;
  }
  return !file.type || ALLOWED_IMAGE_TYPES.has(file.type.toLowerCase());
};

type CampoCuenta =
  | 'ctaInventario'
  | 'ctaVenta'
  | 'ctaCompra'
  | 'ctaCostoVenta'
  | 'ctaDesc'
  | 'ctaCtraPartida';

const ProductoForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codigoArticulo } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [catalogos, setCatalogos] = useState<Catalogos>({
    ramos: [],
    lineas: [],
    clases: [],
    unidadesMedida: []
  });
  const [tiposMoneda, setTiposMoneda] = useState<TipoMoneda[]>([]);
  const [tasasIva, setTasasIva] = useState<number[]>([21, 10.5, 27, 0]);
  const [formData, setFormData] = useState<ProductoFormData>({
    codigoArticulo: '',
    descripcion: '',
    nombreLargo: null,
    codBarra: null,
    codigoProveedor: null,
    codProveedor: null,
    codigoArticuloProveedor: null,
    tipoArticulo: 'RV',
    codigoRamo: '',
    codigoLinea: '',
    codigoClase: '',
    idUnidadMedida: null,
    tipoMoneda: null,
    precioVenta: null,
    precio: null,
    cantidad: null,
    stockMin: null,
    stockMax: null,
    costo: null,
    fechaUltCosto: null,
    fechaUltPrecio: null,
    integrar: null,
    calculaIva: 'S',
    ivaRI: 21,
    ivaRN: null,
    ivaCF: 21,
    ivaEX: 21,
    ivaMT: 21,
    impInterno1: null,
    impInterno2: null,
    tasaHidrica: null,
    porc_Desc: null,
    descuentos: null,
    costoCatalogo: null,
    desc1: null,
    desc2: null,
    desc3: null,
    desc4: null,
    desc5: null,
    precioReceta: null,
    precioUnidad: null,
    atributo1: null,
    atributo2: null,
    atributo3: null,
    sustituto: null,
    reemplazo: null,
    imagen: null,
    lista: null,
    plu: null,
    valorOro: null,
    valorPlata: null,
    porcentajeGanan: null,
    porcentajeMkpminimo: null,
    impuestoInterno: null,
    almacen: null,
    almacenFp: 'F',
    manoObra: null,
    manoObraFp: 'F',
    otro: null,
    otrosFp: 'F',
    stockOptimo: null,
    stockVenta: null,
    stockProduccion: null,
    stockPedido: null,
    recetaUnidad: null,
    relacionPesoUnid: null,
    volumen: null,
    tara: null,
    invetariado: null,
    tipoCodBarra: null,
    coeficiente: null,
    nroTasaIva: 1,
    tiempo: null,
    liquidaComision: null,
    cantOrdenCompra: null,
    calculoPrecio: null,
    noBonificable: null,
    umrefPed: null,
    umrefPrep: null,
    umrefFac: null,
    umrefStock: null,
    anchoCm: null,
    altoCm: null,
    largoCm: null,
    cantPalletUmprep: null,
    sincronizar: null,
    codigoArticuloMeli: null,
    ctaInventario: null,
    ctaVenta: null,
    ctaCompra: null,
    ctaCostoVenta: null,
    ctaDesc: null,
    ctaCtraPartida: null
  });
  const [herenciaDialogOpen, setHerenciaDialogOpen] = useState(false);
  const [pendingRamo, setPendingRamo] = useState<string>('');
  const [imputableSearchOpen, setImputableSearchOpen] = useState(false);
  const [campoCuentaActual, setCampoCuentaActual] = useState<CampoCuenta | null>(null);
  const [imputablesCuentas, setImputablesCuentas] = useState<Record<CampoCuenta, { nroCuenta: number; nomCuenta: string } | null>>({
    ctaInventario: null,
    ctaVenta: null,
    ctaCompra: null,
    ctaCostoVenta: null,
    ctaDesc: null,
    ctaCtraPartida: null
  });
  const [proveedorSearchOpen, setProveedorSearchOpen] = useState(false);
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState<Proveedor | null>(null);
  const [fotosExistentes, setFotosExistentes] = useState<ProductoFoto[]>([]);
  const [fotosPendientes, setFotosPendientes] = useState<PendingProductoFoto[]>([]);
  const [fotoPrincipalKey, setFotoPrincipalKey] = useState<string | null>(null);
  const [fotoIdsEliminadas, setFotoIdsEliminadas] = useState<number[]>([]);

  useEffect(() => {
    console.log('%c 🔄 useEffect ejecutándose', 'background: #222; color: #bada55');
    console.log('%c 📦 codigoArticulo:', 'background: #222; color: #ffa500', codigoArticulo);

    const initializeForm = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('%c 📚 Cargando catálogos...', 'background: #222; color: #00ff00');
        await fetchCatalogos();

        if (codigoArticulo) {
          console.log('%c 🔍 Iniciando carga del producto:', 'background: #222; color: #00ff00', codigoArticulo);
          await loadProducto();
        } else {
          setFotosPendientes((prev) => {
            prev.forEach((foto) => URL.revokeObjectURL(foto.previewUrl));
            return [];
          });
          setFotosExistentes([]);
          setFotoIdsEliminadas([]);
          setFotoPrincipalKey(null);
        }
      } catch (error) {
        console.error('%c ❌ Error en la inicialización:', 'background: #222; color: #ff0000', error);
        setError('Error al inicializar el formulario');
        enqueueSnackbar('Error al cargar los datos del formulario', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    initializeForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo ejecutar al cambiar codigoArticulo
  }, [codigoArticulo]);

  useEffect(() => {
    return () => {
      fotosPendientes.forEach((foto) => URL.revokeObjectURL(foto.previewUrl));
    };
  }, [fotosPendientes]);

  const fetchCatalogos = async () => {
    try {
      setLoading(true);
      const [ramosRes, lineasRes, clasesRes, unidadesRes, tiposMonedaList, tasasIvaList] = await Promise.all([
        api.get('/api/catalogos/ramos', {
          params: {
            pageSize: 1000,
            pageNumber: 1
          }
        }),
        api.get('/api/catalogos/lineas', {
          params: {
            pageSize: 1000,
            pageNumber: 1
          }
        }),
        api.get('/api/catalogos/clases', {
          params: {
            pageSize: 1000,
            pageNumber: 1
          }
        }),
        api.get('/api/catalogos/unidades-medida', {
          params: { pageSize: 1000, pageNumber: 1 },
        }),
        tipoMonedaService.getTiposMoneda(),
        variablesSistemaService.getTasasIva(),
      ]);

      setCatalogos({
        ramos: ramosRes.data.items,
        lineas: lineasRes.data.items,
        clases: clasesRes.data.items,
        unidadesMedida: unidadesRes.data.items ?? [],
      });
      setTiposMoneda(Array.isArray(tiposMonedaList) ? tiposMonedaList : []);
      setTasasIva(Array.isArray(tasasIvaList) && tasasIvaList.length === 4 ? tasasIvaList : [21, 10.5, 27, 0]);
    } catch (error) {
      setError('Error al cargar los catálogos');
      enqueueSnackbar('Error al cargar los catálogos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadProducto = async () => {
    try {
      console.log('%c 🔍 Cargando producto con código:', 'background: #222; color: #00ff00', codigoArticulo);
      
      const response = await api.get(`/api/productos/${codigoArticulo}`);
      console.log('%c ✅ Respuesta del API:', 'background: #222; color: #00ff00', response);
      console.log('%c 📦 Datos del producto:', 'background: #222; color: #00ff00', response.data);

      if (!response.data) {
        throw new Error('No se recibieron datos del producto');
      }

      const d = response.data;
      const num = (v: unknown) => (v != null && v !== '') ? Number(v) : null;
      const str = (v: unknown) => (v != null && v !== '') ? String(v).trim() || null : null;
      const toDate = (v: unknown) => (v ? String(v).slice(0, 10) : null) as string | null;
      const productoData: ProductoFormData = {
        ...formData,
        codigoArticulo: d.codigoArticulo || '',
        descripcion: d.descripcion || '',
        nombreLargo: str(d.nombreLargo),
        codBarra: str(d.codBarra),
        codigoProveedor: str(d.codigoProveedor),
        codProveedor: num(d.codProveedor),
        codigoArticuloProveedor: str(d.codigoArticuloProveedor),
        tipoArticulo: str(d.tipoArticulo),
        codigoRamo: d.codigoRamo || '',
        codigoLinea: d.codigoLinea || '',
        codigoClase: d.codigoClase || '',
        idUnidadMedida: str(d.idUnidadMedida),
        tipoMoneda: num(d.tipoMoneda),
        precioVenta: num(d.precioVenta),
        precio: num(d.precio),
        cantidad: num(d.cantidad),
        stockMin: num(d.stockMin),
        stockMax: num(d.stockMax),
        costo: num(d.costo),
        fechaUltCosto: toDate(d.fechaUltCosto),
        fechaUltPrecio: toDate(d.fechaUltPrecio),
        integrar: num(d.integrar),
        calculaIva: str(d.calculaIva),
        ivaRI: num(d.ivaRI),
        ivaRN: num(d.ivaRN),
        ivaCF: num(d.ivaCF),
        ivaEX: num(d.ivaEX),
        ivaMT: num(d.ivaMT),
        impInterno1: num(d.impInterno1),
        impInterno2: num(d.impInterno2),
        tasaHidrica: num(d.tasaHidrica),
        porc_Desc: num(d.porc_Desc),
        descuentos: Array.isArray(d.descuentos) ? d.descuentos : null,
        costoCatalogo: num((d as any).costoCatalogo ?? d.descuentos?.[0]?.costoCatalogo) ?? num(d.costo),
        desc1: num((d as any).desc1 ?? d.descuentos?.[0]?.desc1),
        desc2: num((d as any).desc2 ?? d.descuentos?.[0]?.desc2),
        desc3: num((d as any).desc3 ?? d.descuentos?.[0]?.desc3),
        desc4: num((d as any).desc4 ?? d.descuentos?.[0]?.desc4),
        desc5: num((d as any).desc5 ?? d.descuentos?.[0]?.desc5),
        precioReceta: num(d.precioReceta),
        precioUnidad: num(d.precioUnidad),
        atributo1: str(d.atributo1),
        atributo2: str(d.atributo2),
        atributo3: str(d.atributo3),
        sustituto: str(d.sustituto),
        reemplazo: str(d.reemplazo),
        imagen: str(d.imagen),
        lista: str(d.lista),
        plu: str(d.plu),
        valorOro: num(d.valorOro),
        valorPlata: num(d.valorPlata),
        porcentajeGanan: num(d.porcentajeGanan),
        porcentajeMkpminimo: num(d.porcentajeMkpminimo),
        impuestoInterno: num(d.impuestoInterno),
        almacen: num(d.almacen),
        almacenFp: str(d.almacenFp),
        manoObra: num(d.manoObra),
        manoObraFp: str(d.manoObraFp),
        otro: num(d.otro),
        otrosFp: str(d.otrosFp),
        stockOptimo: num(d.stockOptimo),
        stockVenta: num(d.stockVenta),
        stockProduccion: num(d.stockProduccion),
        stockPedido: num(d.stockPedido),
        recetaUnidad: str(d.recetaUnidad),
        relacionPesoUnid: num(d.relacionPesoUnid),
        volumen: num(d.volumen),
        tara: num(d.tara),
        invetariado: num(d.invetariado),
        tipoCodBarra: num(d.tipoCodBarra),
        coeficiente: num(d.coeficiente),
        nroTasaIva: num(d.nroTasaIva),
        tiempo: str(d.tiempo),
        liquidaComision: num(d.liquidaComision),
        cantOrdenCompra: num(d.cantOrdenCompra),
        calculoPrecio: str(d.calculoPrecio),
        noBonificable: num(d.noBonificable),
        umrefPed: str(d.umrefPed),
        umrefPrep: str(d.umrefPrep),
        umrefFac: str(d.umrefFac),
        umrefStock: str(d.umrefStock),
        anchoCm: num(d.anchoCm),
        altoCm: num(d.altoCm),
        largoCm: num(d.largoCm),
        cantPalletUmprep: num(d.cantPalletUmprep),
        sincronizar: num(d.sincronizar),
        codigoArticuloMeli: str(d.codigoArticuloMeli),
        ctaInventario: num(d.ctaInventario),
        ctaVenta: num(d.ctaVenta),
        ctaCompra: num(d.ctaCompra),
        ctaCostoVenta: num(d.ctaCostoVenta),
        ctaDesc: num(d.ctaDesc),
        ctaCtraPartida: num(d.ctaCtraPartida)
      };

      // Recalcular precio final según fórmula: si Calcula IVA = Sí → precio neto + % IVA C.F., sino = precio neto (2 decimales)
      const precioFinalCalculado = (() => {
        const pn = productoData.precioVenta;
        if (pn == null || Number.isNaN(Number(pn))) return productoData.precio;
        if (productoData.calculaIva === 'S') {
          const iva = productoData.ivaCF ?? 0;
          return Math.round(Number(pn) * (1 + Number(iva) / 100) * 100) / 100;
        }
        return Math.round(Number(pn) * 100) / 100;
      })();

      console.log('%c 📝 Datos procesados:', 'background: #222; color: #00ff00', productoData);
      const fotosProducto = Array.isArray(d.fotos) ? (d.fotos as ProductoFoto[]) : [];
      const fotoPrincipal = fotosProducto.find((foto) => foto.esPrincipal) ?? fotosProducto[0] ?? null;
      setFormData({ ...productoData, precio: precioFinalCalculado ?? productoData.precio });
      setFotosExistentes(fotosProducto);
      setFotoIdsEliminadas([]);
      setFotoPrincipalKey(fotoPrincipal ? `existing-${fotoPrincipal.id}` : null);

      // Cargar información del proveedor principal para mostrar número y nombre
      if (productoData.codProveedor != null && Number(productoData.codProveedor) > 0) {
        try {
          const prov = await proveedorService.getProveedor(Number(productoData.codProveedor));
          if (prov) {
            setProveedorSeleccionado(prov);
          }
        } catch {
          // Si falla, al menos quedará visible el número de proveedor
        }
      }

      const camposCuenta: CampoCuenta[] = ['ctaInventario', 'ctaVenta', 'ctaCompra', 'ctaCostoVenta', 'ctaDesc', 'ctaCtraPartida'];
      const nuevasEtiquetas: Record<CampoCuenta, { nroCuenta: number; nomCuenta: string } | null> = {
        ctaInventario: null,
        ctaVenta: null,
        ctaCompra: null,
        ctaCostoVenta: null,
        ctaDesc: null,
        ctaCtraPartida: null
      };
      await Promise.all(
        camposCuenta.map(async (campo) => {
          const nro = productoData[campo];
          if (nro != null && Number(nro) > 0) {
            try {
              const imputable = await imputableService.getImputable(Number(nro));
              if (imputable) {
                nuevasEtiquetas[campo] = { nroCuenta: imputable.nroCuenta, nomCuenta: (imputable.nomCuenta || '').trim() };
              }
            } catch {
              // dejar null, se mostrará solo el número
            }
          }
        })
      );
      setImputablesCuentas(nuevasEtiquetas);
    } catch (error: any) {
      console.error('%c ❌ Error al cargar el producto:', 'background: #222; color: #ff0000', error);
      console.error('%c ❌ Mensaje de error:', 'background: #222; color: #ff0000', error.message);
      console.error('%c ❌ Detalles del error:', 'background: #222; color: #ff0000', error.response?.data);
      setError(`Error al cargar el producto: ${error.message}`);
      enqueueSnackbar(`Error al cargar el producto: ${error.message}`, { variant: 'error' });
    }
  };

  const refreshFotosProducto = async (codigo: string) => {
    const fotos = await productoService.getFotos(codigo);
    setFotosExistentes(fotos);
    setFotoIdsEliminadas([]);
    setFotoPrincipalKey((prev) => {
      if (prev?.startsWith('pending-')) {
        return prev;
      }

      const principal = fotos.find((foto) => foto.esPrincipal) ?? fotos[0] ?? null;
      return principal ? `existing-${principal.id}` : null;
    });
  };

  const fotosExistentesActivas = fotosExistentes.filter((foto) => !fotoIdsEliminadas.includes(foto.id));
  const totalFotosConfiguradas = fotosExistentesActivas.length + fotosPendientes.length;
  const cupoRestanteFotos = Math.max(0, MAX_FOTOS_PRODUCTO - totalFotosConfiguradas);
  const contadorColor = totalFotosConfiguradas >= MAX_FOTOS_PRODUCTO ? 'error' : totalFotosConfiguradas >= MAX_FOTOS_PRODUCTO - 1 ? 'warning' : 'primary';

  const handleFotoPrincipalChange = (key: string) => {
    setFotoPrincipalKey(key);
  };

  const handleFotoFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const archivos = Array.from(event.target.files ?? []);
    if (!archivos.length) {
      return;
    }

    const archivosValidos = archivos.filter((file) => isSupportedImageFile(file) && file.size <= MAX_FOTO_BYTES);
    const invalidosFormato = archivos.filter((file) => !isSupportedImageFile(file)).length;
    const invalidosTamano = archivos.filter((file) => file.size > MAX_FOTO_BYTES).length;

    if (invalidosFormato > 0) {
      enqueueSnackbar('Se omitieron imágenes con formato no compatible. Use JPG, PNG o WEBP.', { variant: 'warning' });
    }
    if (invalidosTamano > 0) {
      enqueueSnackbar(`Se omitieron imágenes que superan ${MAX_FOTO_MB} MB por foto.`, { variant: 'warning' });
    }

    if (!archivosValidos.length) {
      event.target.value = '';
      return;
    }

    const disponibles = Math.max(0, MAX_FOTOS_PRODUCTO - totalFotosConfiguradas);
    if (disponibles <= 0) {
      enqueueSnackbar(`Cada producto admite un máximo de ${MAX_FOTOS_PRODUCTO} fotos.`, { variant: 'warning' });
      event.target.value = '';
      return;
    }

    const seleccionados = archivosValidos.slice(0, disponibles).map((file) => ({
      tempId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      previewUrl: URL.createObjectURL(file),
      nombre: file.name,
    }));

    setFotosPendientes((prev) => {
      const next = [...prev, ...seleccionados];
      if (!fotoPrincipalKey && !fotosExistentesActivas.length && next[0]) {
        setFotoPrincipalKey(`pending-${next[0].tempId}`);
      }
      return next;
    });

    if (archivosValidos.length > disponibles) {
      enqueueSnackbar(`Solo se agregaron ${disponibles} fotos porque el máximo es ${MAX_FOTOS_PRODUCTO}.`, { variant: 'info' });
    }

    event.target.value = '';
  };

  const removePendingFoto = (tempId: string) => {
    setFotosPendientes((prev) => {
      const foto = prev.find((item) => item.tempId === tempId);
      if (foto) {
        URL.revokeObjectURL(foto.previewUrl);
      }

      const next = prev.filter((item) => item.tempId !== tempId);
      if (fotoPrincipalKey === `pending-${tempId}`) {
        const fallback = fotosExistentesActivas[0] ? `existing-${fotosExistentesActivas[0].id}` : next[0] ? `pending-${next[0].tempId}` : null;
        setFotoPrincipalKey(fallback);
      }
      return next;
    });
  };

  const removeExistingFoto = (fotoId: number) => {
    setFotoIdsEliminadas((prev) => Array.from(new Set([...prev, fotoId])));
    if (fotoPrincipalKey === `existing-${fotoId}`) {
      const fallbackExisting = fotosExistentesActivas.find((foto) => foto.id !== fotoId);
      const fallbackPending = fotosPendientes[0];
      setFotoPrincipalKey(
        fallbackExisting ? `existing-${fallbackExisting.id}` : fallbackPending ? `pending-${fallbackPending.tempId}` : null,
      );
    }
  };

  /**
   * Fuerza valor numérico. Los TextField devuelven string en onChange; si mezclamos con números,
   * `bruto += alm` puede concatenar (ej. 44000 + "2555" → "440002555").
   */
  const numeroCampo = (v: unknown, fallback = 0): number => {
    if (v == null || v === '') return fallback;
    if (typeof v === 'number') return Number.isFinite(v) ? v : fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  /** Costo catálogo neto de descuentos (sin almacén / MO / otros). */
  const getCostoBrutoTrasDescuentos = (data: ProductoFormData): number => {
    const cat = numeroCampo(data.costoCatalogo ?? data.costo);
    const d1 = numeroCampo(data.desc1) / 100;
    const d2 = numeroCampo(data.desc2) / 100;
    const d3 = numeroCampo(data.desc3) / 100;
    const d4 = numeroCampo(data.desc4) / 100;
    const d5 = numeroCampo(data.desc5) / 100;
    return cat * (1 - d1) * (1 - d2) * (1 - d3) * (1 - d4) * (1 - d5);
  };

  /** Calcula el costo neto a partir de los datos del formulario (misma lógica que el campo solo lectura). */
  const getCostoNeto = (data: ProductoFormData): number => {
    let bruto = getCostoBrutoTrasDescuentos(data);
    const alm = numeroCampo(data.almacen);
    const mo = numeroCampo(data.manoObra);
    const ot = numeroCampo(data.otro);
    const almIsPct = data.almacenFp === 'P';
    const moIsPct = data.manoObraFp === 'P';
    const otIsPct = data.otrosFp === 'P';
    bruto += almIsPct ? bruto * (alm / 100) : alm;
    bruto += moIsPct ? bruto * (mo / 100) : mo;
    bruto += otIsPct ? bruto * (ot / 100) : ot;
    return bruto;
  };

  /** Campos que al cambiar modifican el costo neto y por tanto deben actualizar el precio neto. */
  const CAMPOS_AFECTAN_COSTO_NETO = new Set([
    'costoCatalogo', 'costo', 'desc1', 'desc2', 'desc3', 'desc4', 'desc5',
    'almacen', 'otro', 'manoObra'
  ]);

  /** Precio final = si Calcula IVA = Sí → precio neto + (precio neto × % IVA C.F.), sino = precio neto. Se muestra con 2 decimales. */
  const getPrecioFinal = (data: ProductoFormData): number | null => {
    const rawPn = data.precioVenta;
    const precioNeto = rawPn != null ? Number(rawPn) : null;
    if (precioNeto == null || Number.isNaN(precioNeto)) return null;
    if (data.calculaIva === 'S') {
      const rawIva = data.ivaCF;
      const ivaCF = rawIva != null ? Number(rawIva) : 0;
      return Math.round(precioNeto * (1 + (Number.isNaN(ivaCF) ? 0 : ivaCF) / 100) * 100) / 100;
    }
    return Math.round(precioNeto * 100) / 100;
  };

  const actualizarPrecioNetoDesdeCostoYPorcentaje = (next: ProductoFormData): ProductoFormData => {
    const pct = next.porcentajeGanan != null ? Number(next.porcentajeGanan) : null;
    if (pct == null || Number.isNaN(pct)) return next;
    const costoNeto = getCostoNeto(next);
    const withPrecioVenta = { ...next, precioVenta: Math.round(costoNeto * (1 + pct / 100) * 10000) / 10000 };
    return { ...withPrecioVenta, precio: getPrecioFinal(withPrecioVenta) };
  };

  const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = event.target;
    setFormData(prev => {
      const fieldValue: string | number | null =
        type === 'number'
          ? value === ''
            ? null
            : (() => {
                const n = Number(value);
                return Number.isNaN(n) ? null : n;
              })()
          : value;
      const next = { ...prev, [name]: fieldValue } as ProductoFormData;
      if (name === 'porcentajeGanan') {
        const pct = typeof fieldValue === 'number' && !Number.isNaN(fieldValue) ? fieldValue : null;
        if (pct != null) {
          const costoNeto = getCostoNeto(next);
          const withPrecioVenta = { ...next, precioVenta: Math.round(costoNeto * (1 + pct / 100) * 10000) / 10000 };
          return { ...withPrecioVenta, precio: getPrecioFinal(withPrecioVenta) };
        }
      }
      if (CAMPOS_AFECTAN_COSTO_NETO.has(name)) {
        return actualizarPrecioNetoDesdeCostoYPorcentaje(next);
      }
      if (name === 'precioVenta') {
        const costoNeto = getCostoNeto(next);
        const precioNetoVal = typeof fieldValue === 'number' && !Number.isNaN(fieldValue) ? fieldValue : null;
        if (precioNetoVal != null && !Number.isNaN(precioNetoVal) && costoNeto > 0) {
          const pct = ((precioNetoVal / costoNeto) - 1) * 100;
          const nextWithPct = { ...next, porcentajeGanan: Math.round(pct * 100) / 100 };
          return { ...nextWithPct, precio: getPrecioFinal(nextWithPct) };
        }
        return { ...next, precio: getPrecioFinal(next) };
      }
      if (name === 'precio') {
        const finalVal = typeof fieldValue === 'number' && !Number.isNaN(fieldValue) ? fieldValue : null;
        if (finalVal == null || Number.isNaN(finalVal)) {
          return { ...next, precio: null };
        }

        // 1) Recalcular precio neto desde el precio final y el IVA
        let nuevoPrecioNeto = finalVal;
        if (prev.calculaIva === 'S') {
          const ivaRaw = prev.ivaCF != null ? Number(prev.ivaCF) : 0;
          const ivaPct = Number.isNaN(ivaRaw) ? 0 : ivaRaw;
          const factor = 1 + ivaPct / 100;
          if (factor > 0) {
            nuevoPrecioNeto = finalVal / factor;
          }
        }
        // Limitar precio neto a 4 decimales
        nuevoPrecioNeto = Math.round(nuevoPrecioNeto * 10000) / 10000;

        // 2) Recalcular % ganancia en base al costo neto
        const costoNeto = getCostoNeto({ ...next, precioVenta: nuevoPrecioNeto });
        let nuevoPorcentajeGanan: number | null = next.porcentajeGanan ?? null;
        if (costoNeto > 0 && !Number.isNaN(costoNeto)) {
          const pct = ((nuevoPrecioNeto / costoNeto) - 1) * 100;
          nuevoPorcentajeGanan = Math.round(pct * 100) / 100;
        }

        const nextWithNet = {
          ...next,
          precio: finalVal,
          precioVenta: nuevoPrecioNeto,
          porcentajeGanan: nuevoPorcentajeGanan,
        };
        return nextWithNet;
      }
      if (name === 'ivaCF') {
        return { ...next, precio: getPrecioFinal(next) };
      }
      if (name === 'codigoArticulo' && (!codigoArticulo || codigoArticulo === 'nuevo') && (prev.codigoArticuloProveedor === '' || prev.codigoArticuloProveedor === null)) {
        return { ...next, codigoArticuloProveedor: (typeof value === 'string' ? value : String(value)) || null };
      }
      return next;
    });
  };

  const handleSelectChange = (event: SelectChangeEvent<string | number>) => {
    const { name, value } = event.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'calculaIva') {
        let withIva = next;
        if (value === 'N') {
          withIva = { ...next, ivaRI: 0, ivaMT: 0, ivaCF: 0, ivaEX: 0, ivaRN: 0 };
        } else if (value === 'S') {
          const nro = Math.min(4, Math.max(1, prev.nroTasaIva ?? 1));
          const pct = tasasIva[nro - 1] ?? 0;
          withIva = { ...next, ivaRI: pct, ivaMT: pct, ivaCF: pct, ivaEX: pct };
        }
        return { ...withIva, precio: getPrecioFinal(withIva) };
      }
      return next;
    });
  };

  const handleTasaIvaChange = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    const nro = Math.min(4, Math.max(1, parseInt(value, 10) || 1));
    const pct = tasasIva[nro - 1] ?? 0;
    setFormData(prev => {
      const next = {
        ...prev,
        nroTasaIva: nro,
        ivaRI: pct,
        ivaMT: pct,
        ivaCF: pct,
        ivaEX: pct
      };
      return { ...next, precio: getPrecioFinal(next) };
    });
  };

  const handleRadioChange = (field: 'almacenFp' | 'manoObraFp' | 'otrosFp') => (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setFormData(prev => actualizarPrecioNetoDesdeCostoYPorcentaje({ ...prev, [field]: value }));
  };

  const abrirBuscadorImputable = (campo: CampoCuenta) => {
    setCampoCuentaActual(campo);
    setImputableSearchOpen(true);
  };

  const handleImputableSelect = (imputable: Imputable) => {
    if (!campoCuentaActual) return;

    setFormData(prev => ({
      ...prev,
      [campoCuentaActual]: imputable.nroCuenta
    }));
    setImputablesCuentas(prev => ({
      ...prev,
      [campoCuentaActual]: { nroCuenta: imputable.nroCuenta, nomCuenta: (imputable.nomCuenta || '').trim() }
    }));
    setImputableSearchOpen(false);
  };

  const valorDisplayProveedor = (): string => {
    if (proveedorSeleccionado) {
      const nombre = (proveedorSeleccionado.nombre || '').trim();
      return `${proveedorSeleccionado.nroProveedor} – ${nombre}`;
    }
    const num = formData.codProveedor;
    return num != null ? String(num) : '';
  };

  const valorDisplayCuenta = (campo: CampoCuenta): string => {
    const sel = imputablesCuentas[campo];
    if (sel) return `${sel.nroCuenta} - ${sel.nomCuenta || ''}`;
    const num = formData[campo];
    return num != null ? String(num) : '';
  };

  const handleRamoChange = (event: SelectChangeEvent<string | number>) => {
    const { value } = event.target;
    const nuevoRamo = (value ?? '') as string;

    // Guardamos el ramo seleccionado
    setPendingRamo(nuevoRamo);

    // Siempre actualizamos el ramo seleccionado en el formulario
    setFormData(prev => ({
      ...prev,
      codigoRamo: nuevoRamo
    }));

    // Si no hay ramo seleccionado, no hacemos nada más
    if (!nuevoRamo) {
      return;
    }

    // Para cualquier artículo (nuevo o existente), heredar automáticamente
    // las cuentas contables desde el ramo seleccionado
    void aplicarHerenciaDesdeRamo(true);
  };

  const aplicarHerenciaDesdeRamo = async (heredar: boolean) => {
    const codigoRamoSeleccionado = pendingRamo;
    setHerenciaDialogOpen(false);

    if (!codigoRamoSeleccionado) {
      return;
    }

    // Siempre actualizamos el ramo seleccionado en el formulario
    setFormData(prev => ({
      ...prev,
      codigoRamo: codigoRamoSeleccionado
    }));

    if (!heredar) {
      return;
    }

    try {
      const ramo: Ramo = await catalogosService.getRamo(codigoRamoSeleccionado);

      setFormData(prev => ({
        ...prev,
        ctaInventario: ramo.ctaInventario != null ? Number(ramo.ctaInventario) : prev.ctaInventario ?? null,
        ctaVenta: ramo.ctaVenta != null ? Number(ramo.ctaVenta) : prev.ctaVenta ?? null,
        ctaCompra: ramo.ctaCompra != null ? Number(ramo.ctaCompra) : prev.ctaCompra ?? null,
        ctaCostoVenta: ramo.ctaCostoVenta != null ? Number(ramo.ctaCostoVenta) : prev.ctaCostoVenta ?? null,
        ctaDesc: ramo.ctaDesc != null ? Number(ramo.ctaDesc) : prev.ctaDesc ?? null,
        ctaCtraPartida: ramo.ctaCtraPartida != null ? Number(ramo.ctaCtraPartida) : prev.ctaCtraPartida ?? null
      }));

      const camposCuenta: CampoCuenta[] = ['ctaInventario', 'ctaVenta', 'ctaCompra', 'ctaCostoVenta', 'ctaDesc', 'ctaCtraPartida'];
      const valores: Record<CampoCuenta, number | null> = {
        ctaInventario: ramo.ctaInventario != null ? Number(ramo.ctaInventario) : null,
        ctaVenta: ramo.ctaVenta != null ? Number(ramo.ctaVenta) : null,
        ctaCompra: ramo.ctaCompra != null ? Number(ramo.ctaCompra) : null,
        ctaCostoVenta: ramo.ctaCostoVenta != null ? Number(ramo.ctaCostoVenta) : null,
        ctaDesc: ramo.ctaDesc != null ? Number(ramo.ctaDesc) : null,
        ctaCtraPartida: ramo.ctaCtraPartida != null ? Number(ramo.ctaCtraPartida) : null
      };
      const nuevasEtiquetas: Record<CampoCuenta, { nroCuenta: number; nomCuenta: string } | null> = {
        ctaInventario: null,
        ctaVenta: null,
        ctaCompra: null,
        ctaCostoVenta: null,
        ctaDesc: null,
        ctaCtraPartida: null
      };
      await Promise.all(
        camposCuenta.map(async (campo) => {
          const nro = valores[campo];
          if (nro != null && nro > 0) {
            try {
              const imputable = await imputableService.getImputable(nro);
              if (imputable) {
                nuevasEtiquetas[campo] = { nroCuenta: imputable.nroCuenta, nomCuenta: (imputable.nomCuenta || '').trim() };
              }
            } catch {
              nuevasEtiquetas[campo] = { nroCuenta: nro, nomCuenta: '' };
            }
          }
        })
      );
      setImputablesCuentas(nuevasEtiquetas);

      enqueueSnackbar('Datos contables heredados desde el ramo seleccionado', { variant: 'info' });
    } catch (error) {
      console.error('Error al heredar datos del ramo:', error);
      enqueueSnackbar('Error al heredar los datos del ramo', { variant: 'error' });
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setSaving(true);
      // Propiedades que no se envían al endpoint; costoCatalogo y desc1..desc5 van en la raíz, no en array descuentos
      const {
        cantidad,
        precioReceta,
        precioUnidad,
        lista,
        codigoProveedor,
        imagen,
        descuentos,
        fechaUltCosto,
        fechaUltPrecio,
        stockVenta,
        stockProduccion,
        stockPedido,
        ...payload
      } = formData as any;

      const codigoProducto = codigoArticulo || formData.codigoArticulo;
      if (codigoArticulo) {
        await api.put(`/api/productos/${codigoArticulo}`, payload);
      } else {
        await api.post('/api/productos', payload);
      }

      for (const fotoId of fotoIdsEliminadas) {
        await productoService.deleteFoto(codigoProducto, fotoId);
      }

      const fotoPendientePrincipal = fotoPrincipalKey?.startsWith('pending-')
        ? fotoPrincipalKey.replace('pending-', '')
        : null;

      const pendientesOrdenadas = [...fotosPendientes].sort((a, b) => {
        if (a.tempId === fotoPendientePrincipal) return -1;
        if (b.tempId === fotoPendientePrincipal) return 1;
        return 0;
      });

      for (const foto of pendientesOrdenadas) {
        await productoService.uploadFoto(codigoProducto, {
          archivo: foto.file,
          esPrincipal: foto.tempId === fotoPendientePrincipal,
        });
      }

      if (fotoPrincipalKey?.startsWith('existing-')) {
        const fotoId = Number(fotoPrincipalKey.replace('existing-', ''));
        if (!Number.isNaN(fotoId) && !fotoIdsEliminadas.includes(fotoId)) {
          await productoService.setFotoPrincipal(codigoProducto, fotoId);
        }
      }

      fotosPendientes.forEach((foto) => URL.revokeObjectURL(foto.previewUrl));
      setFotosPendientes([]);

      if (codigoProducto) {
        await refreshFotosProducto(codigoProducto);
      }

      enqueueSnackbar(codigoArticulo ? 'Producto actualizado con éxito' : 'Producto creado con éxito', { variant: 'success' });
      navigate('/inventario/productos');
    } catch (err: any) {
      console.error('Error al guardar el producto:', err);
      const apiMessage = err?.response?.data?.message;
      const message = typeof apiMessage === 'string' && apiMessage
        ? apiMessage
        : err?.message || 'Error al guardar el producto';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 3,
              fontWeight: 600,
              color: '#1976d2'
            }}
          >
            {codigoArticulo ? 'Editar Producto' : 'Nuevo Producto'}
          </Typography>

          <form onSubmit={handleSubmit}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="product tabs">
                <Tab label="General" />
                <Tab label="Costos y Precios" />
                <Tab label="Impuestos" />
                <Tab label="Cuentas" />
                <Tab label="Stock" />
                <Tab label="Logística" />
                <Tab label="Códigos" />
                <Tab label="eCommerce" />
              </Tabs>
            </Box>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                name="codigoArticulo"
                label="Código"
                value={formData.codigoArticulo}
                onChange={handleTextFieldChange}
                fullWidth
                required
                disabled={!!codigoArticulo}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                name="descripcion"
                label="Descripción"
                value={formData.descripcion}
                onChange={handleTextFieldChange}
                fullWidth
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="tipo-articulo-select-label">Tipo de Artículo</InputLabel>
                <Select
                  labelId="tipo-articulo-select-label"
                  name="tipoArticulo"
                  value={formData.tipoArticulo || ''}
                  onChange={handleSelectChange}
                  label="Tipo de Artículo"
                >
                  <MenuItem value="">Seleccione un tipo</MenuItem>
                  {TIPO_ARTICULO_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.invetariado}
                    onChange={(e) => setFormData((prev) => ({ ...prev, invetariado: e.target.checked ? 1 : 0 }))}
                    color="primary"
                  />
                }
                label="Inventariado"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.liquidaComision}
                    onChange={(e) => setFormData((prev) => ({ ...prev, liquidaComision: e.target.checked ? 1 : 0 }))}
                    color="primary"
                  />
                }
                label="Liquida Comisión"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.noBonificable}
                    onChange={(e) => setFormData((prev) => ({ ...prev, noBonificable: e.target.checked ? 1 : 0 }))}
                    color="primary"
                  />
                }
                label="No Bonificable"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="ramo-select-label">Ramo</InputLabel>
                <Select
                  labelId="ramo-select-label"
                  name="codigoRamo"
                  value={formData.codigoRamo}
                  onChange={handleRamoChange}
                  label="Ramo"
                  required
                >
                  <MenuItem value="">Seleccione un ramo</MenuItem>
                  {catalogos.ramos.map((ramo) => (
                    <MenuItem key={ramo.codigoRamo} value={ramo.codigoRamo}>
                      {ramo.nombreRamo}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="linea-select-label">Línea</InputLabel>
                <Select
                  labelId="linea-select-label"
                  name="codigoLinea"
                  value={formData.codigoLinea}
                  onChange={handleSelectChange}
                  label="Línea"
                >
                  <MenuItem value="">Seleccione una línea</MenuItem>
                  {catalogos.lineas.map((linea) => (
                    <MenuItem key={linea.codigoLinea} value={linea.codigoLinea}>
                      {linea.nombreLinea}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="clase-select-label">Clase</InputLabel>
                <Select
                  labelId="clase-select-label"
                  name="codigoClase"
                  value={formData.codigoClase}
                  onChange={handleSelectChange}
                  label="Clase"
                >
                  <MenuItem value="">Seleccione una clase</MenuItem>
                  {catalogos.clases.map((clase) => (
                    <MenuItem key={clase.codigoClase} value={clase.codigoClase}>
                      {clase.nombreClase}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}><TextField name="atributo1" label="Atributo 1" value={formData.atributo1 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={12} md={4}><TextField name="atributo2" label="Atributo 2" value={formData.atributo2 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={12} md={4}><TextField name="atributo3" label="Atributo 3" value={formData.atributo3 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={12}>
              <TextField
                name="nombreLargo"
                label="Nombre Largo"
                value={formData.nombreLargo || ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="umref-ped-select-label">UM Ref. Pedido</InputLabel>
                <Select
                  labelId="umref-ped-select-label"
                  name="umrefPed"
                  value={formData.umrefPed || ''}
                  onChange={handleSelectChange}
                  label="UM Ref. Pedido"
                >
                  <MenuItem value="">Seleccione una unidad</MenuItem>
                  {catalogos.unidadesMedida.map((unidad) => (
                    <MenuItem key={`umrefPed-${unidad.idUnidadMedida}`} value={unidad.idUnidadMedida}>
                      {unidad.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="umref-prep-select-label">UM Ref. Prep</InputLabel>
                <Select
                  labelId="umref-prep-select-label"
                  name="umrefPrep"
                  value={formData.umrefPrep || ''}
                  onChange={handleSelectChange}
                  label="UM Ref. Prep"
                >
                  <MenuItem value="">Seleccione una unidad</MenuItem>
                  {catalogos.unidadesMedida.map((unidad) => (
                    <MenuItem key={`umrefPrep-${unidad.idUnidadMedida}`} value={unidad.idUnidadMedida}>
                      {unidad.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="umref-fac-select-label">UM Ref. Factura</InputLabel>
                <Select
                  labelId="umref-fac-select-label"
                  name="umrefFac"
                  value={formData.umrefFac || ''}
                  onChange={handleSelectChange}
                  label="UM Ref. Factura"
                >
                  <MenuItem value="">Seleccione una unidad</MenuItem>
                  {catalogos.unidadesMedida.map((unidad) => (
                    <MenuItem key={`umrefFac-${unidad.idUnidadMedida}`} value={unidad.idUnidadMedida}>
                      {unidad.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}><TextField name="cantPalletUmprep" label="Cant. por Pallet UM Prep" type="number" value={formData.cantPalletUmprep ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={3}><TextField name="anchoCm" label="Ancho (cm)" type="number" value={formData.anchoCm ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={3}><TextField name="altoCm" label="Alto (cm)" type="number" value={formData.altoCm ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={3}><TextField name="largoCm" label="Largo (cm)" type="number" value={formData.largoCm ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={3}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="umref-stock-select-label">UM Ref. Stock</InputLabel>
                <Select
                  labelId="umref-stock-select-label"
                  name="umrefStock"
                  value={formData.umrefStock || ''}
                  onChange={handleSelectChange}
                  label="UM Ref. Stock"
                >
                  <MenuItem value="">Seleccione una unidad</MenuItem>
                  {catalogos.unidadesMedida.map((unidad) => (
                    <MenuItem key={`umrefStock-${unidad.idUnidadMedida}`} value={unidad.idUnidadMedida}>
                      {unidad.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={3}><TextField name="relacionPesoUnid" label="Peso por Unidad" type="number" value={formData.relacionPesoUnid ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={3}><TextField name="volumen" label="Volumen" type="number" value={formData.volumen ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={3}><TextField name="tara" label="Tara" type="number" value={formData.tara ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                name="ctaInventario"
                label="Cuenta Inventario"
                value={valorDisplayCuenta('ctaInventario')}
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => abrirBuscadorImputable('ctaInventario')}
                        edge="end"
                        color="primary"
                        title="Buscar cuenta contable"
                        sx={{ borderRadius: 2 }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="ctaVenta"
                label="Cuenta Venta"
                value={valorDisplayCuenta('ctaVenta')}
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => abrirBuscadorImputable('ctaVenta')}
                        edge="end"
                        color="primary"
                        title="Buscar cuenta contable"
                        sx={{ borderRadius: 2 }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="ctaCompra"
                label="Cuenta Compra"
                value={valorDisplayCuenta('ctaCompra')}
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => abrirBuscadorImputable('ctaCompra')}
                        edge="end"
                        color="primary"
                        title="Buscar cuenta contable"
                        sx={{ borderRadius: 2 }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="ctaCostoVenta"
                label="Cuenta Costo Venta"
                value={valorDisplayCuenta('ctaCostoVenta')}
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => abrirBuscadorImputable('ctaCostoVenta')}
                        edge="end"
                        color="primary"
                        title="Buscar cuenta contable"
                        sx={{ borderRadius: 2 }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="ctaDesc"
                label="Cuenta Descuento"
                value={valorDisplayCuenta('ctaDesc')}
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => abrirBuscadorImputable('ctaDesc')}
                        edge="end"
                        color="primary"
                        title="Buscar cuenta contable"
                        sx={{ borderRadius: 2 }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="ctaCtraPartida"
                label="Cuenta Contrapartida"
                value={valorDisplayCuenta('ctaCtraPartida')}
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => abrirBuscadorImputable('ctaCtraPartida')}
                        edge="end"
                        color="primary"
                        title="Buscar cuenta contable"
                        sx={{ borderRadius: 2 }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={2}>
            {/* === COSTOS === */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} color="text.primary" sx={{ mb: 2 }}>Costos</Typography>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                name="costoCatalogo"
                label="Costo Catalogo"
                type="number"
                value={formData.costoCatalogo ?? formData.costo ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  const n = v === '' ? null : Number(v);
                  setFormData(prev => actualizarPrecioNetoDesdeCostoYPorcentaje({ ...prev, costoCatalogo: n, costo: n }));
                }}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.0001 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Buscar costo catálogo"><SearchIcon fontSize="small" /></IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField name="desc1" label="% Desc 1" type="number" value={formData.desc1 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" inputProps={{ step: 0.01 }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField name="desc2" label="% Desc 2" type="number" value={formData.desc2 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" inputProps={{ step: 0.01 }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField name="desc3" label="% Desc 3" type="number" value={formData.desc3 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" inputProps={{ step: 0.01 }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField name="desc4" label="% Desc 4" type="number" value={formData.desc4 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" inputProps={{ step: 0.01 }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField name="desc5" label="% Desc 5" type="number" value={formData.desc5 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" inputProps={{ step: 0.01 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Costo Bruto"
                value={getCostoBrutoTrasDescuentos(formData).toLocaleString('es-AR', { minimumFractionDigits: 4 })}
                fullWidth
                margin="normal"
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <TextField name="almacen" label="Almacenamiento" type="number" value={formData.almacen ?? ''} onChange={handleTextFieldChange} sx={{ width: 140 }} size="small" inputProps={{ step: 0.01 }} />
                <RadioGroup row value={formData.almacenFp || 'F'} onChange={handleRadioChange('almacenFp')}>
                  <FormControlLabel value="F" control={<Radio size="small" />} label="Monto Fijo x unidad" />
                  <FormControlLabel value="P" control={<Radio size="small" />} label="Porcentaje" />
                </RadioGroup>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <TextField name="otro" label="Otros Costos" type="number" value={formData.otro ?? ''} onChange={handleTextFieldChange} sx={{ width: 140 }} size="small" inputProps={{ step: 0.01 }} />
                <RadioGroup row value={formData.otrosFp || 'F'} onChange={handleRadioChange('otrosFp')}>
                  <FormControlLabel value="F" control={<Radio size="small" />} label="Monto Fijo x unidad" />
                  <FormControlLabel value="P" control={<Radio size="small" />} label="Porcentaje" />
                </RadioGroup>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <TextField name="manoObra" label="Mano de Obra" type="number" value={formData.manoObra ?? ''} onChange={handleTextFieldChange} sx={{ width: 140 }} size="small" inputProps={{ step: 0.01 }} />
                <RadioGroup row value={formData.manoObraFp || 'F'} onChange={handleRadioChange('manoObraFp')}>
                  <FormControlLabel value="F" control={<Radio size="small" />} label="Monto Fijo x unidad" />
                  <FormControlLabel value="P" control={<Radio size="small" />} label="Porcentaje" />
                </RadioGroup>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Costo Neto"
                value={getCostoNeto(formData).toLocaleString('es-AR', { minimumFractionDigits: 4 })}
                fullWidth
                margin="normal"
                InputProps={{ readOnly: true }}
                InputLabelProps={{ sx: { fontWeight: 700 } }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' }, '& .MuiInputBase-input': { fontWeight: 700 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField name="fechaUltCosto" label="Fecha Último Costo" type="date" value={formData.fechaUltCosto || ''} fullWidth margin="normal" InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 3 }} />
            </Grid>
            {/* === PRECIO DE VENTA === */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} color="text.primary" sx={{ mb: 2 }}>Precio de venta</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField name="porcentajeGanan" label="% Ganancia" type="number" value={formData.porcentajeGanan ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" sx={{ maxWidth: 280 }} inputProps={{ step: 0.01 }} InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="precioVenta"
                label="Precio Neto"
                type="number"
                value={formData.precioVenta ?? ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.0001 }}
                InputLabelProps={{ sx: { fontWeight: 700 } }}
                sx={{ '& .MuiInputBase-input': { fontWeight: 700 }}}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField name="fechaUltPrecio" label="Fecha Último Precio" type="date" value={formData.fechaUltPrecio || ''} fullWidth margin="normal" InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="precio"
                label="Precio Final (IVA CF)"
                type="number"
                value={formData.precio ?? ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField name="porcentajeMkpminimo" label="% Mkp Mínimo" type="number" value={formData.porcentajeMkpminimo ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" inputProps={{ step: 0.01 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField name="porc_Desc" label="% Descuento" type="number" value={formData.porc_Desc ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" inputProps={{ step: 0.01 }} />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="tipo-moneda-select-label">Tipo Moneda</InputLabel>
                <Select
                  labelId="tipo-moneda-select-label"
                  id="tipo-moneda-select"
                  value={formData.tipoMoneda ?? ''}
                  label="Tipo Moneda"
                  onChange={(e: SelectChangeEvent<number | string>) => {
                    const v = e.target.value;
                    setFormData(prev => ({ ...prev, tipoMoneda: v === '' ? null : Number(v) }));
                  }}
                >
                  <MenuItem value="">
                    <em>Ninguno</em>
                  </MenuItem>
                  {tiposMoneda.map((t) => {
                    const val = t.codigoMoneda ?? t.tipoMoneda ?? 0;
                    const label = t.nombre ?? t.descripcion ?? t.simbolo ?? String(val);
                    return (
                      <MenuItem key={val} value={val}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField name="calculoPrecio" label="Cálculo Precio" value={formData.calculoPrecio ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth margin="normal" sx={{ '& .MuiInputLabel-root': { fontWeight: 700 }, '& .MuiSelect-select': { fontWeight: 700 } }}>
                <InputLabel id="calculaIva-select-label">Calcula IVA</InputLabel>
                <Select
                  labelId="calculaIva-select-label"
                  name="calculaIva"
                  value={formData.calculaIva || ''}
                  onChange={handleSelectChange}
                  label="Calcula IVA"
                >
                  <MenuItem value="S">Sí</MenuItem>
                  <MenuItem value="N">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl component="fieldset" margin="normal" sx={{ width: '100%' }} disabled={formData.calculaIva === 'N'}>
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Tasa IVA</Typography>
                <RadioGroup row value={String(formData.nroTasaIva ?? 1)} onChange={handleTasaIvaChange}>
                  {[1, 2, 3, 4].map((n) => (
                    <FormControlLabel
                      key={n}
                      value={String(n)}
                      control={<Radio size="small" />}
                      label={`${tasasIva[n - 1] ?? 0}%`}
                      sx={{ mr: 2 }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={2}>
              <TextField name="ivaRI" label="IVA R.I." type="number" value={formData.ivaRI ?? ''} fullWidth margin="normal" InputProps={{ readOnly: true }} sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }} />
            </Grid>
            <Grid item xs={2}>
              <TextField name="ivaMT" label="IVA M.T." type="number" value={formData.ivaMT ?? ''} fullWidth margin="normal" InputProps={{ readOnly: true }} sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }} />
            </Grid>
            <Grid item xs={2}>
              <TextField name="ivaCF" label="IVA C.F." type="number" value={formData.ivaCF ?? ''} fullWidth margin="normal" InputProps={{ readOnly: true }} sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }} />
            </Grid>
            <Grid item xs={2}>
              <TextField name="ivaEX" label="IVA Exento" type="number" value={formData.ivaEX ?? ''} fullWidth margin="normal" InputProps={{ readOnly: true }} sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }} />
            </Grid>
            <Grid item xs={2}>
              <TextField name="ivaRN" label="IVA R.N." type="number" value={formData.ivaRN ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={2}>
              <TextField name="impInterno1" label="Imp. Interno 1" type="number" value={formData.impInterno1 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" />
            </Grid>
            <Grid item xs={2}>
              <TextField name="impInterno2" label="Imp. Interno 2" type="number" value={formData.impInterno2 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" />
            </Grid>
            <Grid item xs={2}>
              <TextField name="tasaHidrica" label="Tasa Hídrica" type="number" value={formData.tasaHidrica ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" />
            </Grid>
            <Grid item xs={2}>
              <TextField name="impuestoInterno" label="% Impuesto Interno" type="number" value={formData.impuestoInterno ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" inputProps={{ step: 0.01, min: 0, max: 100 }} />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Grid container spacing={2}>
          <Grid item xs={4}>
              <TextField
                name="cantidad"
                label="Cantidad Actual"
                type="number"
                value={formData.cantidad ?? ''}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01 }}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } , '& .MuiInputBase-input': { fontWeight: 700 } }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="normal" sx={{ '& .MuiInputLabel-root': { fontWeight: 700 }, '& .MuiSelect-select': { fontWeight: 700 } }}>
                <InputLabel id="unidad-medida-select-label">Unidad de Medida</InputLabel>
                <Select
                  labelId="unidad-medida-select-label"
                  name="idUnidadMedida"
                  value={formData.idUnidadMedida || ''}
                  onChange={handleSelectChange}
                  label="Unidad de Medida"
                >
                  <MenuItem value="">Seleccione una unidad</MenuItem>
                  {catalogos.unidadesMedida.map((unidad) => (
                    <MenuItem key={unidad.idUnidadMedida} value={unidad.idUnidadMedida}>
                      {unidad.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={4}>
              <TextField
                name="stockVenta"
                label="Stock Disponible para la venta"
                type="number"
                value={formData.stockVenta ?? ''}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01 }}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                name="stockPedido"
                label="Stock Reservado"
                type="number"
                value={formData.stockPedido ?? ''}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01 }}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }}
              />
            </Grid>
            <Grid item xs={4}></Grid>
            <Grid item xs={4}>
              <TextField
                name="stockProduccion"
                label="Stock de Producción"
                type="number"
                value={formData.stockProduccion ?? ''}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01 }}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                name="cantOrdenCompra"
                label="Stock en Orden Compra"
                type="number"
                value={formData.cantOrdenCompra ?? ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01 }}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                name="stockDisponiblePromesa"
                label="Stock Disponible para Promesa (ATP)"
                type="number"
                value={
                  (Number(formData.cantidad ?? 0) - Number(formData.stockPedido ?? 0)) +
                  Number(formData.stockProduccion ?? 0) +
                  Number(formData.cantOrdenCompra ?? 0)
                }
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01 }}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={4}>
              <TextField
                name="stockMin"
                label="Stock Mínimo"
                type="number"
                value={formData.stockMin ?? ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                name="stockMax"
                label="Stock Máximo"
                type="number"
                value={formData.stockMax ?? ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                name="stockOptimo"
                label="Stock Óptimo"
                type="number"
                value={formData.stockOptimo ?? ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                name="codBarra"
                label="Código de Barras"
                value={formData.codBarra || ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="plu-select-label">PLU</InputLabel>
                <Select
                  labelId="plu-select-label"
                  name="plu"
                  value={formData.plu || ''}
                  onChange={handleSelectChange}
                  label="PLU"
                >
                  <MenuItem value="No usa">No usa</MenuItem>
                  <MenuItem value="Cantidad">Cantidad</MenuItem>
                  <MenuItem value="Precio">Precio</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="codProveedor"
                label="Proveedor principal"
                value={valorDisplayProveedor()}
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setProveedorSearchOpen(true)}
                        edge="end"
                        color="primary"
                        title="Buscar proveedor principal"
                        sx={{ borderRadius: 2 }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText="Seleccione el proveedor principal usando el buscador."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="codigoArticuloProveedor"
                label="Código Artículo Proveedor"
                value={formData.codigoArticuloProveedor || ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}><TextField name="sustituto" label="Sustituto" value={formData.sustituto ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={7}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.sincronizar}
                    onChange={(e) => setFormData((prev) => ({ ...prev, sincronizar: e.target.checked ? 1 : 0 }))}
                    color="primary"
                  />
                }
                label="Sincronizar con el ecommerce"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="codigoArticuloMeli"
                label="Cód. Artículo MercadoLibre"
                value={formData.codigoArticuloMeli ?? ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ border: '2px dashed #d0d7e2', borderRadius: 3, p: 3, background: '#f8fafc' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Fotos del producto
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Principal + secundarias. Máximo {MAX_FOTOS_PRODUCTO} fotos.
                    </Typography>
                  </Box>
                  <Chip
                    color={contadorColor}
                    variant={totalFotosConfiguradas >= MAX_FOTOS_PRODUCTO ? 'filled' : 'outlined'}
                    label={`${totalFotosConfiguradas} de ${MAX_FOTOS_PRODUCTO}`}
                    sx={{ fontWeight: 700 }}
                  />
                </Box>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                  <label htmlFor="producto-fotos-upload">
                    <input
                      id="producto-fotos-upload"
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleFotoFilesSelected}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<CloudUploadIcon />}
                      component="span"
                      disabled={totalFotosConfiguradas >= MAX_FOTOS_PRODUCTO}
                      sx={{ fontWeight: 700, minWidth: 180 }}
                    >
                      Agregar fotos
                    </Button>
                  </label>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  <Chip size="small" label="Formatos: JPG, PNG, WEBP" />
                  <Chip size="small" label={`Máx: ${MAX_FOTO_MB} MB`} />
                  <Chip size="small" label="Ideal: 1:1 · 1200x1200 px+" />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Se generan versiones automáticas para web (miniatura y vista mediana).
                </Typography>

                <Grid container spacing={2}>
                  {fotosExistentesActivas.map((foto) => (
                    <Grid item xs={12} sm={6} md={3} key={`existing-${foto.id}`}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderColor: fotoPrincipalKey === `existing-${foto.id}` ? 'primary.main' : 'divider',
                          borderWidth: 1,
                          boxShadow: fotoPrincipalKey === `existing-${foto.id}` ? '0 0 0 2px rgba(25,118,210,0.2)' : 'none',
                        }}
                      >
                        <CardContent sx={{ pb: 1 }}>
                          <Box sx={{ position: 'relative', mb: 1 }}>
                            {fotoPrincipalKey === `existing-${foto.id}` && (
                              <Chip
                                size="small"
                                color="primary"
                                icon={<StarIcon sx={{ fontSize: 16 }} />}
                                label="Principal"
                                sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}
                              />
                            )}
                            <Box
                              component="img"
                              src={foto.thumbnailUrl}
                              alt={foto.nombreArchivoOriginal || `Foto ${foto.id}`}
                              sx={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 2, bgcolor: 'grey.100' }}
                              onError={(e: any) => {
                                e.currentTarget.src = getProductoFallbackImage();
                              }}
                            />
                          </Box>
                        </CardContent>
                        <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'space-between' }}>
                          <Button
                            size="small"
                            variant={fotoPrincipalKey === `existing-${foto.id}` ? 'contained' : 'outlined'}
                            startIcon={fotoPrincipalKey === `existing-${foto.id}` ? <StarIcon /> : <StarBorderIcon />}
                            onClick={() => handleFotoPrincipalChange(`existing-${foto.id}`)}
                          >
                            Principal
                          </Button>
                          <IconButton color="error" onClick={() => removeExistingFoto(foto.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}

                  {fotosPendientes.map((foto) => (
                    <Grid item xs={12} sm={6} md={3} key={`pending-${foto.tempId}`}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderColor: fotoPrincipalKey === `pending-${foto.tempId}` ? 'primary.main' : 'divider',
                          borderWidth: 1,
                          boxShadow: fotoPrincipalKey === `pending-${foto.tempId}` ? '0 0 0 2px rgba(25,118,210,0.2)' : 'none',
                        }}
                      >
                        <CardContent sx={{ pb: 1 }}>
                          <Box sx={{ position: 'relative', mb: 1 }}>
                            {fotoPrincipalKey === `pending-${foto.tempId}` && (
                              <Chip
                                size="small"
                                color="primary"
                                icon={<StarIcon sx={{ fontSize: 16 }} />}
                                label="Principal"
                                sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}
                              />
                            )}
                            <Box
                              component="img"
                              src={foto.previewUrl}
                              alt={foto.nombre}
                              sx={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 2, bgcolor: 'grey.100' }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Se sube al guardar
                          </Typography>
                        </CardContent>
                        <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'space-between' }}>
                          <Button
                            size="small"
                            variant={fotoPrincipalKey === `pending-${foto.tempId}` ? 'contained' : 'outlined'}
                            startIcon={fotoPrincipalKey === `pending-${foto.tempId}` ? <StarIcon /> : <StarBorderIcon />}
                            onClick={() => handleFotoPrincipalChange(`pending-${foto.tempId}`)}
                          >
                            Principal
                          </Button>
                          <IconButton color="error" onClick={() => removePendingFoto(foto.tempId)}>
                            <DeleteIcon />
                          </IconButton>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {totalFotosConfiguradas === 0 && (
                  <Box
                    sx={{
                      mt: 2,
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      textAlign: 'center',
                      backgroundColor: 'background.paper',
                    }}
                  >
                    <PhotoLibraryOutlinedIcon color="disabled" sx={{ fontSize: 30, mb: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      Todavía no hay fotos configuradas para este producto.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Te quedan {cupoRestanteFotos} de {MAX_FOTOS_PRODUCTO} disponibles.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

            <Dialog
              open={herenciaDialogOpen}
              onClose={() => aplicarHerenciaDesdeRamo(false)}
            >
              <DialogTitle>¿Heredar datos del ramo?</DialogTitle>
              <DialogContent>
                <Typography variant="body2">
                  ¿Desea heredar los datos contables del ramo seleccionado (Cta. Inventario, Cta. Venta, Cta. Compra, Cta. Costo Venta, Cta. Descuento y Cta. Contrapartida) al nuevo producto?
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => aplicarHerenciaDesdeRamo(false)} color="inherit">
                  No heredar
                </Button>
                <Button onClick={() => aplicarHerenciaDesdeRamo(true)} color="primary" autoFocus>
                  Sí, heredar
                </Button>
              </DialogActions>
            </Dialog>

            <ImputableSearch
              open={imputableSearchOpen}
              onClose={() => setImputableSearchOpen(false)}
              onSelect={handleImputableSelect}
            />

            <ProveedorSearch
              open={proveedorSearchOpen}
              onClose={() => setProveedorSearchOpen(false)}
              onSelect={(proveedor) => {
                setFormData(prev => ({
                  ...prev,
                  codProveedor: proveedor?.nroProveedor ?? null
                }));
                setProveedorSeleccionado(proveedor);
              }}
            />

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(21, 128, 61, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(21, 128, 61, 0.4)',
                  }
                }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/inventario/productos')}
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProductoForm;